<template>
  <GMapMap
    @click="handleMapClick"
    ref="mapRef"
    :center="pathStartFinish.pickup"
    :zoom="zoomLevel"
    map-type-id="styledMap"
    class="mt-3 h-80 w-full"
    :options="{
      disableDefaultUI: true,
      zoomControl: true,
      fullscreenControl: true
    }"
  >
    <GMapPolyline v-if="path" :path="path" :options="{ strokeColor: '#14a201' }" />
    <GMapMarker
      :key="pathStartFinish.pickup"
      @click="handleMarkerClick('pickup')"
      @dragstart="handleMarkerClick('pickup')"
      :position="pathStartFinish.pickup"
      :icon="actualMarkerPickup"
      :draggable="true"
      @dragend="updateMarkerLocation('pickup', $event.latLng)"
    />
    <GMapMarker
      @click="handleMarkerClick('dropoff')"
      @dragstart="handleMarkerClick('dropoff')"
      :position="pathStartFinish.dropoff"
      :icon="actualMarkerDropoff"
      :draggable="true"
      @dragend="updateMarkerLocation('dropoff', $event.latLng)"
    />
  </GMapMap>
  <template v-if="modal && isGMStylePresent">
    <teleport :to="modalTarget">
      <map-modal @close-modal="closeModal" />
    </teleport>
  </template>
</template>

<script setup>
import { computed, inject, onMounted, ref, watch } from 'vue'
import marker from '~project_assets/images/marker.svg'

import { storeToRefs } from 'pinia'
import * as turf from '@turf/turf'
import EUGeoJson from '@/assets/countries.geojson'
import MapModal from '@/components/layout/modals/mapModal.vue'
import { useTrustyStore } from '@/stores/data/trustyComplete'

const trustyStore = useTrustyStore()

const { activeAuto, pathStartFinish, pickupRef, dropoffRef } = storeToRefs(trustyStore)
const { checkAddress, updateLastChoisePlace, updatePlaceChoised } = trustyStore

const axios = inject('axios')
const mapRef = ref(null)
const requestTime = ref(false)
const path = ref(null)
const latRoadAbs = ref(null)
let mapInstance = ref()
let zoomLevel = ref(8)
const actualMarkerPickup = ref(marker)
const actualMarkerDropoff = ref(marker)
const prevPickup = ref(null)
const prevDropoff = ref(null)
const modal = ref(false)
const modalTarget = ref('.gm-style')
const emit = defineEmits(['markerdragged'])

const isGMStylePresent = computed(() => {
  return document.querySelector(modalTarget.value) !== null
})

const props = defineProps({
  form: Object,
  formName: String
})

function closeModal() {
  modal.value = false
}

function saveToLocalStorage() {
  localStorage.setItem('prevPickup', JSON.stringify(prevPickup.value))
  localStorage.setItem('prevDropoff', JSON.stringify(prevDropoff.value))
}

function loadFromLocalStorage() {
  const storedPickup = localStorage.getItem('prevPickup')
  const storedDropoff = localStorage.getItem('prevDropoff')

  if (storedPickup) {
    prevPickup.value = JSON.parse(storedPickup)
  }

  if (storedDropoff) {
    prevDropoff.value = JSON.parse(storedDropoff)
  }
}

function handleMarkerClick(marker) {
  activeAuto.value = marker
}
async function loadEUGeoJson() {
  const response = await fetch(EUGeoJson)
  return response.json()
}

async function updateMarkerLocation(locationType, newLocation) {
  let latLng = {
    lat: newLocation.lat(),
    lng: newLocation.lng()
  }

  if (!latLng.lat || !latLng.lng) {
    console.error('Ошибка: неверные координаты.')
    return
  }

  const italyGeo = await loadEUGeoJson()

  let isInItaly = false
  for (const feature of italyGeo.features) {
    const geometry = feature.geometry

    if (geometry.type === 'Polygon') {
      const polygon = turf.polygon(geometry.coordinates)
      if (turf.booleanPointInPolygon(turf.point([latLng.lng, latLng.lat]), polygon)) {
        isInItaly = true
        break
      }
    } else if (geometry.type === 'MultiPolygon') {
      for (let coords of geometry.coordinates) {
        const polygon = turf.polygon(coords)
        if (turf.booleanPointInPolygon(turf.point([latLng.lng, latLng.lat]), polygon)) {
          isInItaly = true
          break
        }
      }
    }
  }

  if (isInItaly) {
    prevPickup.value = pathStartFinish.value.pickup
    prevDropoff.value = pathStartFinish.value.dropoff
    saveToLocalStorage()
  }

  if (!isInItaly) {
    modal.value = true
    if (locationType === 'pickup') {
      pathStartFinish.value.pickup = { ...prevPickup.value }
    } else if (locationType === 'dropoff') {
      pathStartFinish.value.dropoff = { ...prevDropoff.value }
    }
    return
  }

  actualMarkerPickup.value = marker
  actualMarkerDropoff.value = marker
  emit('markerdragged', locationType)

  // eslint-disable-next-line
  const geocoder = new google.maps.Geocoder()

  geocoder.geocode({ location: latLng }, (results, status) => {
    // eslint-disable-next-line
    if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
      const firstResult = results[0]
      if (!checkAddress(firstResult, locationType)) return false

      if (locationType === 'pickup' && pickupRef.value) {
        pickupRef.value = firstResult.formatted_address
        updateLastChoisePlace(
          {
            formatted_address: pickupRef.value,
            place_id: firstResult.place_id
          },
          locationType,
          true
        )
        updatePlaceChoised(true, locationType)
        pathStartFinish.value.pickup.lat = firstResult.geometry.location.lat()
        pathStartFinish.value.pickup.lng = firstResult.geometry.location.lng()
        props.form.validate()
      } else if (locationType === 'dropoff' && dropoffRef.value) {
        dropoffRef.value = firstResult.formatted_address
        updateLastChoisePlace(
          {
            formatted_address: dropoffRef.value,
            place_id: firstResult.place_id
          },
          locationType,
          true
        )
        updatePlaceChoised(true, locationType)
        pathStartFinish.value.dropoff.lat = firstResult.geometry.location.lat()
        pathStartFinish.value.dropoff.lng = firstResult.geometry.location.lng()
        props.form.validate()
      }
    } else {
      console.error('Ошибка геокодирования: нет результатов или ошибка в запросе.')
    }
  })
}

function fitMapBounds() {
  if (
    mapRef.value &&
    pathStartFinish.value.pickup &&
    pathStartFinish.value.dropoff &&
    mapRef.value.$mapObject
  ) {
    // eslint-disable-next-line
    const bounds = new google.maps.LatLngBounds()
    bounds.extend(pathStartFinish.value.pickup)
    bounds.extend(pathStartFinish.value.dropoff)

    let diffLat = Math.abs(pathStartFinish.value.pickup.lat - pathStartFinish.value.dropoff.lat)
    const diffLong = Math.abs(pathStartFinish.value.pickup.lng - pathStartFinish.value.dropoff.lng)
    if (diffLong < 0.01) {
      bounds.extend({
        lat: pathStartFinish.value.pickup.lat,
        lng: pathStartFinish.value.pickup.lng + 1
      })
      bounds.extend({
        lat: pathStartFinish.value.pickup.lat,
        lng: pathStartFinish.value.pickup.lng - 1
      })
    }
    mapRef.value.$mapObject.fitBounds(bounds)
    zoomLevel.value =
      mapRef.value.$mapObject.getZoom() -
      ((props.form.values.pickup === 'Florence, Metropolitan City of Florence, Italy' &&
        props.form.values.dropoff === 'Lucca, Province of Lucca, Italy') ||
      (props.form.values.pickup === 'Florence, Metropolitan City of Florence, Italy' &&
        props.form.values.dropoff === 'Pisa, Province of Pisa, Italy')
        ? 0.7
        : 0)

    if (diffLong > diffLat) {
      if (zoomLevel.value > 10) {
        zoomLevel.value -= 1
      } else if (latRoadAbs.value >= 0.2 && latRoadAbs.value < 0.4 && zoomLevel.value >= 9.5) {
        zoomLevel.value -= 1
      } else if (latRoadAbs.value >= 0.4 && latRoadAbs.value < 0.6 && zoomLevel.value >= 9) {
        zoomLevel.value -= 0.85
      } else if (latRoadAbs.value >= 0.6 && latRoadAbs.value < 0.7 && zoomLevel.value >= 8) {
        zoomLevel.value -= 1.2
      } else if (latRoadAbs.value >= 0.7 && latRoadAbs.value < 0.8 && zoomLevel.value >= 8) {
        zoomLevel.value -= 1
      } else if (latRoadAbs.value >= 0.8 && latRoadAbs.value < 1.3 && zoomLevel.value >= 8) {
        zoomLevel.value -= 1
      } else if (latRoadAbs.value >= 1.3 && latRoadAbs.value < 1.5 && zoomLevel.value >= 7) {
        zoomLevel.value -= 0.8
      } else if (latRoadAbs.value >= 1.5 && latRoadAbs.value < 2.2 && zoomLevel.value >= 7) {
        zoomLevel.value -= 0.4
      }
    } else {
      if (zoomLevel.value > 10) {
        zoomLevel.value -= 1.5
      }
    }
  }
}

watch(
  pathStartFinish.value,
  (value) => {
    if (
      Object.prototype.hasOwnProperty.call(value, 'pickup') &&
      Object.prototype.hasOwnProperty.call(value, 'dropoff')
    ) {
      requestTime.value = true

      axios
        .post('/route/path', {
          origin: `${value.pickup.lat},${value.pickup.lng}`,
          destination: `${value.dropoff.lat},${value.dropoff.lng}`,
          key: import.meta.env.VITE_APP_GOOGLE_API_KEY
        })
        .then((value) => {
          requestTime.value = false

          if (!value.data?.data?.routes.length) return false

          // eslint-disable-next-line
          path.value = new google.maps.geometry.encoding.decodePath(
            value.data.data.routes[0].overview_polyline.points
          )
          fitMapBounds()
        })
        .catch((reason) => {
          console.error('Error fetching route path:', reason)
        })
    }
  },
  { deep: true }
)

onMounted(() => {
  loadFromLocalStorage()
  mapRef.value.$mapPromise.then((map) => {
    mapInstance.value = map

    fetch(EUGeoJson)
      .then((response) => response.json())
      .then((geoJsonData) => {
        // eslint-disable-next-line
        const geoJsonLayer = new google.maps.Data()
        geoJsonLayer.addGeoJson(geoJsonData)

        geoJsonLayer.setStyle({
          fillColor: 'transparent',
          strokeWeight: 0.2,
          strokeColor: 'transparent',
          fillOpacity: 0.5
        })

        geoJsonLayer.setMap(map)
      })
      .catch((error) => {
        console.error('Ошибка при загрузке GeoJSON:', error)
      })
    // eslint-disable-next-line
    const styledMapType = new google.maps.StyledMapType([
      {
        featureType: 'administrative',
        elementType: 'labels.text.fill',
        stylers: [
          {
            color: '#444444'
          }
        ]
      },
      {
        featureType: 'landscape',
        elementType: 'all',
        stylers: [
          {
            color: '#f2f2f2'
          }
        ]
      },
      {
        featureType: 'poi',
        elementType: 'all',
        stylers: [
          {
            visibility: 'off'
          }
        ]
      },
      {
        featureType: 'road',
        elementType: 'all',
        stylers: [
          {
            saturation: -100
          },
          {
            lightness: 45
          }
        ]
      },
      {
        featureType: 'road.highway',
        elementType: 'all',
        stylers: [
          {
            visibility: 'simplified'
          }
        ]
      },
      {
        featureType: 'road.arterial',
        elementType: 'labels.icon',
        stylers: [
          {
            visibility: 'off'
          }
        ]
      },
      {
        featureType: 'transit',
        elementType: 'all',
        stylers: [
          {
            visibility: 'off'
          }
        ]
      },
      {
        featureType: 'water',
        elementType: 'all',
        stylers: [
          {
            color: '#46bcec'
          },
          {
            visibility: 'on'
          }
        ]
      }
    ])

    map.mapTypes.set('styledMap', styledMapType)
  })

  fitMapBounds()
  window.vueGoogleMapsInit()

  requestTime.value = true
  if ((props.formName == 'Tours' || props.formName == 'Hourly') && !pathStartFinish.value.pickup) {
    return
  }

  axios
    .post('/route/path', {
      origin: `${pathStartFinish.value.pickup.lat},${pathStartFinish.value.pickup.lng}`,
      destination: `${pathStartFinish.value.dropoff.lat},${pathStartFinish.value.dropoff.lng}`,
      key: import.meta.env.VITE_APP_GOOGLE_API_KEY
    })
    .then((value) => {
      requestTime.value = false

      const routeData = value?.data?.data?.routes?.[0]

      if (!pathStartFinish.value.pickup.lat || !pathStartFinish.value.pickup.lng) {
        loadItalyMap()
        return
      }

      // eslint-disable-next-line
      path.value = new google.maps.geometry.encoding.decodePath(routeData.overview_polyline.points)

      if (routeData.legs?.[0]?.steps) {
        let steps = routeData.legs[0].steps

        let latMin = steps.reduce(function (x, y) {
          return Math.min(x, y.start_location.lat)
        }, 1000)

        let latMax = steps.reduce(function (x, y) {
          return Math.max(x, y.start_location.lat)
        }, 0)

        latRoadAbs.value = latMax - latMin
      }

      setTimeout(() => {
        fitMapBounds()
      }, 200)
    })
    .catch((reason) => {
      console.error('Ошибка при запросе маршрута:', reason)
    })
})

function loadItalyMap() {
  if (!mapInstance.value) return
  const italyCenter = { lat: 41.9028, lng: 12.4964 }
  mapInstance.value.setCenter(italyCenter)
  mapInstance.value.setZoom(6)
}
</script>
<style></style>
